import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EPackageTiers } from '@feathr/blackbox';
import { Nav, NavItem, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import EventSwitcher from '@feathr/extender/components/EventSwitcher';
import { useLicense, useMinPackageTooltip } from '@feathr/extender/hooks';
import { useLocalUrl } from '@feathr/extender/state';
import { getEventIdFromUrl } from '@feathr/extender/utils';

import EventContentNav from './EventContentNav';
import EventMarketingNav from './EventMarketingNav';
import EventPartnersNav from './EventPartnersNav';
import EventSettingsNav from './EventSettingsNav';

import styles from './EventNav.css';
interface ITooltipMessages {
  flights?: string;
  partners?: string;
}

type TTooltipByFeatureReturn = ITooltipMessages | undefined;

function EventNav(): JSX.Element {
  const localUrl = useLocalUrl();
  const eventId = getEventIdFromUrl();
  const { t } = useTranslation();
  const { invites, flights: hasFlights } = useLicense(['invites', 'flights']);

  const partnersTooltip = useMinPackageTooltip('partnerManager', 'invites');

  const tooltipsByFeature: TTooltipByFeatureReturn = useMemo(
    () => ({
      partners: invites ? undefined : partnersTooltip,
    }),
    [invites, partnersTooltip],
  );

  return (
    <>
      <EventSwitcher className={styles.select} />
      {!!eventId && (
        <Nav indented={true} label={'Project navigation'} type={'vertical'}>
          <NavMenu
            hasDefaultChild={true}
            menu={<EventMarketingNav />}
            to={localUrl(`/projects/${eventId}/campaigns`)}
          >
            {t('Marketing')}
          </NavMenu>
          <TrackBlocked
            featureName={'flights'}
            locked={!hasFlights}
            requiredPackage={hasFlights ? undefined : EPackageTiers.Advanced}
          >
            <NavItem
              to={localUrl(`/projects/${eventId}/flights`)}
              tooltip={tooltipsByFeature?.flights}
            >
              {t('Flights')}
            </NavItem>
          </TrackBlocked>
          {!invites ? (
            <TrackBlocked
              featureName={'partners'}
              locked={!invites}
              requiredPackage={invites ? undefined : EPackageTiers.Essential}
            >
              <NavItem
                to={localUrl(`/projects/${eventId}/partners`)}
                tooltip={tooltipsByFeature?.partners}
              >
                {t('Partners')}
              </NavItem>
            </TrackBlocked>
          ) : (
            <NavMenu menu={<EventPartnersNav />} to={localUrl(`/projects/${eventId}/partners`)}>
              {t('Partners')}
            </NavMenu>
          )}
          <NavMenu menu={<EventContentNav />} to={localUrl(`/projects/${eventId}/content`)}>
            {t('Content')}
          </NavMenu>
          <NavItem to={localUrl(`/projects/${eventId}/report`)}>{t('Report')}</NavItem>
          <NavMenu menu={<EventSettingsNav />} to={localUrl(`/projects/${eventId}/settings`)}>
            {t('Project Settings')}
          </NavMenu>
        </Nav>
      )}
    </>
  );
}

export default observer(EventNav);
